import React, { useState } from "react";

import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import {
  useGetApplicationQuery,
  useUpdateIndividualApplicationMutation,
  useGetOfferedDentalPlansQuery,
  useGetOfferedVisionPlansQuery,
} from "IndividualApplication/api";
import Chrome from "IndividualApplication/Chrome";
import { doneURL } from "IndividualApplication/urls";
import { individualURLHelper, sourceLocation, AnalyticsEvent } from "IndividualApplication/utils";
import { Form } from "react-final-form";
import { useHistory } from "react-router-dom";
import { validateRequired, validateRoutingNumber, composeValidators } from "Utils/Validations";

import { IndividualCostReviewTable } from "../Components/IndividualCostReviewTable";
import IndividualDependentEnrollmentTable from "../Components/IndividualDependentEnrollmentTable";
import { IndividualNavSection } from "../Components/IndividualNavSection";
import { IndividualSSNSection } from "../Components/IndividualSSNSection";
import { Body1 } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Surface, SurfaceHeader, SurfaceSection } from "Cobalt/Surface";
import AgentHeader from "Common/Agent/AgentHeader";
import { SIFinalFormField } from "Common/forms";
import { Row, Col } from "Common/Grid";

const focusOnErrors = createDecorator();

// Tooltips
const costToApplication = {
  self_cost: "ee_self",
  spouse_cost: "ee_spouse",
  child_cost: "ee_children",
  family_cost: "ee_family",
};

export const AddPaymentInformationForm = () => (
  <SurfaceSection title="Payment Information">
    <Row>
      <SIFinalFormField label="Bank Name" name="bankName" validate={validateRequired} sm={4} />
    </Row>
    <Row>
      <SIFinalFormField label="Routing Number" name="bankRoutingNumber" validate={composeValidators(validateRequired, validateRoutingNumber)} sm={4} />
    </Row>
    <Row>
      <SIFinalFormField label="Account Number" name="bankAccountNumber" validate={validateRequired} sm={4} />
    </Row>
  </SurfaceSection>
);

const maskExistingSSN = (number) => `XXX-XX-${number.slice(-4)}`;

export const IndividualEnterInformation = () => {
  const history = useHistory();
  const { data: individualApplication, isLoading } = useGetApplicationQuery({});
  const { data: dentalPlans = [] } = useGetOfferedDentalPlansQuery();
  const { data: visionPlans = [] } = useGetOfferedVisionPlansQuery();
  const [coverageSelected, setCoverageSelected] = useState("self_cost");
  const [enrollSelectError, setEnrollSelectError] = useState(false);
  const dependents = individualApplication?.employeeForm?.employeeDependents?.map((d) => ({
    ...d,
    socialSecurityNumber: d.socialSecurityNumber && maskExistingSSN(d.socialSecurityNumber),
  }));
  const filteredDependents = dependents?.filter((d) => {
    if (coverageSelected == "family_cost") return true;
    if (coverageSelected == "child_cost" && d.dependentType == "child") return true;
    if (coverageSelected == "spouse_cost" && d.dependentType == "spouse") return true;
  });
  const socialSecurityNumber = individualApplication?.socialSecurityNumber && maskExistingSSN(individualApplication.socialSecurityNumber);

  const [updateIndividualApplication] = useUpdateIndividualApplicationMutation();

  const { bankName, bankRoutingNumber, bankAccountNumber } = individualApplication || {};

  const initialValues = {
    bankName,
    bankRoutingNumber,
    bankAccountNumber,
    dependents: filteredDependents,
    socialSecurityNumber,
  };

  const dentalPlan = dentalPlans.find((p) => p.id === individualApplication?.individualDentalPlanID);
  const visionPlan = visionPlans.find((p) => p.id === individualApplication?.individualVisionPlanID);

  const onSubmit = async (values) => {
    if (!coverageSelected) {
      setEnrollSelectError(true);
      return false;
    }
    if (enrollSelectError) {
      setEnrollSelectError(false);
    }

    await updateIndividualApplication({
      employeeDependents: (values.dependents || [])
        .filter((d) => !d.socialSecurityNumber.includes("X"))
        .map((d) => ({
          id: d.id,
          socialSecurityNumber: d.socialSecurityNumber,
        })),
      dependentEnrollmentDental: dentalPlan ? costToApplication[coverageSelected] : null,
      dependentEnrollmentVision: visionPlan ? costToApplication[coverageSelected] : null,
      ...values,
    });

    if (individualApplication.individualDentalPlanID) {
      AnalyticsEvent("individual_dental_submitted");
    }
    if (individualApplication.individualVisionPlanID) {
      AnalyticsEvent("individual_vision_submitted");
    }

    const sourceLocationData = sourceLocation();
    if (sourceLocationData.source === "ee-application") {
      window.location.pathname = sourceLocationData.source_url;
    } else {
      history.push(individualURLHelper(doneURL));
    }
  };

  return (
    <Chrome title="Enter Information">
      <Surface mt={5}>
        <SurfaceHeader>Enter Information</SurfaceHeader>
        <AgentHeader>
          Almost done! Please provide a bank account for the first month's premium payment. Payment is usually debited within 1-5 days of application approval.
          Please note that Insurance Carriers will only charge you if the application is approved.
        </AgentHeader>
        <Form
          onSubmit={onSubmit}
          decorators={[focusOnErrors]}
          initialValues={initialValues}
          mutators={{
            ...arrayMutators,
          }}
        >
          {({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              {!isLoading && <IndividualCostReviewTable coverageSelected={coverageSelected} dentalPlan={dentalPlan} visionPlan={visionPlan} />}
              <SurfaceSection title="Enrollment Selections">
                <Row>
                  <Col xs={8} lg={8}>
                    <IndividualDependentEnrollmentTable
                      individualApplication={individualApplication}
                      coverageSelected={coverageSelected}
                      onChange={(value) => {
                        setCoverageSelected(value);
                        setEnrollSelectError(false);
                      }}
                    />
                    {enrollSelectError && (
                      <Body1 pt={4} className="text-error-900">
                        Required
                      </Body1>
                    )}
                  </Col>
                </Row>
              </SurfaceSection>
              {filteredDependents?.length > 0 && <IndividualSSNSection individualApplication={individualApplication} dependents={filteredDependents} />}
              <AddPaymentInformationForm />
              <SurfaceSection>
                <IndividualNavSection page="enterInformation" submitButton={<Button type="submit">Submit Application</Button>} />
              </SurfaceSection>
            </form>
          )}
        </Form>
      </Surface>
    </Chrome>
  );
};
